import { useState, useCallback } from 'react'
import { debounce } from 'lodash'
import { MessageSquare, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { AppHeader } from '@/components/layout/app-header'
import { Main } from '@/components/layout/main'
import { CreateTicketModal } from './components/create-ticket-modal'
import { TicketsTable } from './components/tickets-table'
import { useTicketsQuery } from './hooks/use-tickets-query'
import { tokens } from '@/locales/translations/tokens'

export default function Tickets() {
  const { t } = useTranslation()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [searchFilter, setSearchFilter] = useState('')
  const [debouncedSearchFilter, setDebouncedSearchFilter] = useState('')



  const debouncedSetSearch = useCallback(
    (value: string) => {
      const debounced = debounce(() => {
        setDebouncedSearchFilter(value)
      }, 500)
      debounced()
    },
    []
  )

  const { data, error } = useTicketsQuery({
    page: 1,
    limit: 50,
    status:
      statusFilter === 'all'
        ? undefined
        : (statusFilter as 'open' | 'in_progress' | 'blocked' | 'resolved'),
    type:
      typeFilter === 'all'
        ? undefined
        : (typeFilter as
            | 'technical_problems'
            | 'integration_problems'
            | 'security_issues'
            | 'suggestions'
            | 'others'),
    search: debouncedSearchFilter || undefined,
  })

  const handleSearchChange = (value: string) => {
    setSearchFilter(value)
    debouncedSetSearch(value)
  }



  const filteredTickets = data?.data?.tickets || []

  if (error) {
    return (
      <>
        <AppHeader fixed />
        <Main fixed>
          <div className='flex flex-col items-center justify-center py-16 text-center'>
            <div className='mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-red-100'>
              <MessageSquare className='h-10 w-10 text-red-600' />
            </div>
            <h3 className='mb-2 text-lg font-semibold text-gray-900'>
              {t(tokens.tickets.error.load_title)}
            </h3>
            <p className='max-w-md text-gray-500'>
              {t(tokens.tickets.error.load_description)}
            </p>
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <AppHeader fixed />
      <Main fixed>
        <div className='space-y-6'>
          <div className='flex items-center justify-between'>
            <div>
              <h1 className='text-2xl font-bold'>{t(tokens.tickets.page.title)}</h1>
              <p>{t(tokens.tickets.page.subtitle)}</p>
            </div>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className='mr-2 h-4 w-4' />
              {t(tokens.tickets.create.button)}
            </Button>
          </div>

          <div className='flex flex-col gap-4 sm:flex-row'>
            <div className='flex-1'>
              <Input
                placeholder={t(tokens.tickets.search.placeholder)}
                value={searchFilter}
                onChange={(e) => handleSearchChange(e.target.value)}
                className='max-w-sm'
              />
            </div>
            <div className='flex gap-2'>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className='w-[180px]'>
                  <SelectValue placeholder={t(tokens.tickets.filter.status.placeholder)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t(tokens.tickets.filter.status.all)}</SelectItem>
                  <SelectItem value='open'>{t(tokens.tickets.filter.status.open)}</SelectItem>
                  <SelectItem value='in_progress'>{t(tokens.tickets.filter.status.in_progress)}</SelectItem>
                  <SelectItem value='blocked'>{t(tokens.tickets.filter.status.blocked)}</SelectItem>
                  <SelectItem value='resolved'>{t(tokens.tickets.filter.status.resolved)}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className='w-[180px]'>
                  <SelectValue placeholder={t(tokens.tickets.filter.type.placeholder)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='all'>{t(tokens.tickets.filter.type.all)}</SelectItem>
                  <SelectItem value='technical_problems'>
                    {t(tokens.tickets.filter.type.technical_problems)}
                  </SelectItem>
                  <SelectItem value='integration_problems'>
                    {t(tokens.tickets.filter.type.integration_problems)}
                  </SelectItem>
                  <SelectItem value='security_issues'>
                    {t(tokens.tickets.filter.type.security_issues)}
                  </SelectItem>
                  <SelectItem value='suggestions'>{t(tokens.tickets.filter.type.suggestions)}</SelectItem>
                  <SelectItem value='others'>{t(tokens.tickets.filter.type.others)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          <TicketsTable tickets={filteredTickets} />

          <CreateTicketModal
            open={isCreateModalOpen}
            onOpenChange={setIsCreateModalOpen}
          />
        </div>
      </Main>
    </>
  )
}
